// GERADO de backend-senne/src/contracts — NÃO editar; rode scripts/sync-contracts.sh

/**
 * Spec 20261015-160924 (owner, 2026-10-15: "cada categoria, página e seção no ERP entra no rbac sobre o que cada um
 * vê"): the ERP's categories, pages and sections (a section is a tab inside a page), each one a permission code.
 *
 * A grant on a node covers the node and everything under it; a deny on a node removes it and everything under it,
 * and wins over a grant above. A person sees a category or a page when any node inside it is covered.
 *
 * `observability:read` and `audit:read` are the tabs of Observabilidade and keep their own codes, because the API
 * already decides by them and a hospital administrator holds `audit:read` at HOSPITAL scope outside the ERP.
 */
export type ErpNodeKind = 'category' | 'page' | 'section'

export interface ErpNode {
    readonly codigo: string
    readonly label: string
    readonly kind: ErpNodeKind
    readonly children: readonly ErpNode[]
}

const section = (codigo: string, label: string): ErpNode => ({
    codigo,
    label,
    kind: 'section',
    children: [],
})

export const ERP_TREE: readonly ErpNode[] = [
    {
        codigo: 'erp:agenda',
        label: 'Agenda',
        kind: 'category',
        // The Grade keeps `erp:operate` (P20EE-115) until Henrique decides its code (spec Clarification 3).
        children: [
            {
                codigo: 'erp:operate',
                label: 'Grade',
                kind: 'page',
                children: [],
            },
        ],
    },
    {
        codigo: 'erp:carlao',
        label: 'Carlão',
        kind: 'category',
        children: [
            {
                codigo: 'erp:carlao:config',
                label: 'Configuração',
                kind: 'page',
                children: [
                    // Spec 20261028-133000 AC-14: "Não realizamos" became "Recusas com motivo"; AC-10: a fifth tab.
                    section('erp:carlao:config:recusas', 'Recusas com motivo'),
                    section('erp:carlao:config:regras', 'Acréscimo por regra'),
                    section('erp:carlao:config:rotinas', 'Rotinas'),
                    section('erp:carlao:config:sinonimos', 'Sinônimos'),
                    section('erp:carlao:config:ignorar', 'Ignorar'),
                ],
            },
            {
                codigo: 'erp:carlao:analytics',
                label: 'Analytics',
                kind: 'page',
                children: [
                    section('erp:carlao:analytics:analises', 'Analytics'),
                    section('erp:carlao:analytics:skills', 'Gestão de Skills'),
                ],
            },
        ],
    },
    {
        codigo: 'erp:sistema',
        label: 'Sistema',
        kind: 'category',
        children: [
            {
                codigo: 'erp:sistema:observabilidade',
                label: 'Observabilidade',
                kind: 'page',
                children: [
                    section('observability:read', 'Erros'),
                    section('audit:read', 'Acessos'),
                ],
            },
            // Spec 20261028-134000 AC-2, AC-6: Sistema › API, the OpenAPI document of the backend.
            { codigo: 'api:docs', label: 'API', kind: 'page', children: [] },
        ],
    },
]

const walk = (nodes: readonly ErpNode[]): ErpNode[] =>
    nodes.flatMap((node) => [node, ...walk(node.children)])

/** Every code in the tree, in the tree's order. */
export const ERP_TREE_CODES: readonly string[] = walk(ERP_TREE).map(
    (node) => node.codigo
)

/** The node with `codigo` and every node under it, or nothing when the code is outside the tree. */
export function erpCodesUnder(codigo: string): string[] {
    const node = walk(ERP_TREE).find((n) => n.codigo === codigo)
    return node ? walk([node]).map((n) => n.codigo) : []
}

/**
 * Codes the API checks without the `admin:full` shortcut (spec AC-2). `erp:operate` stays out until Henrique decides
 * the Grade's code: his routes and tests admit `admin:full` (P20EE-115), and the owner left that call to him
 * (2026-10-15, "Fica com o Henrique").
 */
export const ERP_STRICT_CODES: readonly string[] = ERP_TREE_CODES.filter(
    (codigo) => codigo !== 'erp:operate'
)
